/*
    implements api for maphub backend endpoints
 */
import {elog, setClientContextID} from "@/engine/helpers/logging.js";
import {CN_TYPE_GEOMETRY_OP} from "@/engine/comm/sseCM.js";
import * as tus from 'tus-js-client'

export class MapApi {
    constructor() {
        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };
        this.version = 'v1';
        this.clientConxtextID = this.clientNewContextID();
        setClientContextID(this.clientConxtextID);
    }

    /**
     * returns userspace object or throw the error
     * @returns {Promise<unknown>}
     */
    async apiGetUserspace(data){
        return this._apiRequest(
            "/" + this.version + "/userspace",
            "GET",
            data)
    }

    /**
     * Requests a replacement short-lived data-access token for the active session.
     * @returns {Promise<unknown>} Backend response containing the token object in data.
     * @throws {Error|Object} Surfaces network, response parsing, and backend API errors.
     */
    async apiGetShortLivedToken() {
        return this._apiRequest(
            "/" + this.version + "/short-lived-token",
            "GET",
            {})
    }

    /**
     * creates raster Area from file
     * @param source - uploadedId or download url
     * @param workspaceId - workspaceId or null, if null the new workspace will be created on success
     * @returns {Promise<T>}
     */
    async apiCreateLayer(source, type, workspaceId) {
        let url = (workspaceId) ? `/${this.version}/workspaces/${workspaceId}/layers` : `/${this.version}/layers`
        return this._apiRequest(
            url,
            "POST",
            {file: source, type: type})
    }

    async apiDeleteLayer( layer ){
        return this._apiRequest(
            `/${this.version}/workspaces/${layer.workspace.id}/layers/${layer.id}`,
            "DELETE",
            {})
    }

    /**
     * updates Layer name
     * @param {Layer} layer
     * @returns {Promise<T>}
     */
    async apiUpdateLayer( layer ){

        let data = {
            name: layer.name,
            srs: layer.srs,
            style: JSON.stringify(layer.style)
        }
        if (layer.dataType) {
            data.dataType = layer.dataType
        }
        return this._apiRequest(
            `/${this.version}/workspaces/${layer.workspace.id}/layers/${layer.id}`,
            "PUT",
            data)
    }

    /**
     * uploads file using tusd service
     * @param task - task name (is added to file metadata)
     * @param file - file description object (as get from input field)
     * @param progressCb
     * @param successCb(uploadId)
     * @param errorCb
     */
    fileUpload(task, file, progressCb, successCb, errorCb) {
        const maxFileSize = 524288000;
        //example from https://github.com/tus/tus-js-client/blob/master/docs/usage.md
        let errCb = function (error) {
            elog("DEBUG","Failed because: " + error)
            if (typeof errorCb === "function")
                errorCb(error);
        }
        if (!file) {
            errorCb("File isn't defined")
            return;
        }

        if (!file.size > maxFileSize) {
            errorCb(`The file size is more than ${maxFileSize}. Use cloud storage for uploading`);
            return;
        }

        let upload = null;
        upload = new tus.Upload(file, {
            // Endpoint is the upload creation URL from your tus server
            endpoint: "/upload/files/",
            // Retry delays will enable tus-js-client to automatically retry on errors
            retryDelays: [0, 3000, 5000, 10000, 20000],
            // Attach additional meta data about the file for the server
            metadata: {
                filename: file.name,
                filetype: file.type,
                task: task
            },
            onError: function(error) {
                errCb(error)
            },
            onProgress: function(bytesUploaded, bytesTotal) {
                let percentage = (bytesUploaded / bytesTotal * 100).toFixed(2)
                //elog("DEBUG", `Uploading progress ${bytesUploaded} from ${bytesTotal}, ${percentage}%`)
                if (typeof progressCb === "function")
                    progressCb(bytesUploaded, bytesTotal, percentage)
            },
            onSuccess: function() {
                //elog("DEBUG", `Uploaded ${upload.file.name} from ${upload.url}`)
                if (typeof successCb === "function")
                    successCb(upload.url.substring(upload.url.lastIndexOf("/") + 1))
            }
        })

        // Check if there are any previous uploads to continue.
        upload.findPreviousUploads().then(function (previousUploads) {
            // Found previous uploads so we select the first one.
            if (previousUploads.length) {
                upload.resumeFromPreviousUpload(previousUploads[0])
            }

            // Start the upload
            upload.start()
        })
    }

    /**
     * get workspace by Id
     * @param workspaceId
     * @returns {Promise<T>}
     */
    async apiGetWorkspace( workspaceId ){
        return this._apiRequest(
            `/${this.version}/workspaces/${workspaceId}`,
            "GET",
            {}).then( apiData => {
            return apiData.data
        })
    }

    async apiCreateWorkspace(name, bounds) {
        let data = {}
        if (name)
            data.name = name

        if (bounds) {
            data.bounds = {
                min: bounds.getSouthWest(),
                max: bounds.getNorthEast()
            }
        }

        return this._apiRequest(
            "/" + this.version + "/workspaces",
            "POST",
            data).then( apiData => {
            return apiData.data
        })
    }

    async apiDeleteWorkspace( workspace ){
        return this._apiRequest(
            `/${this.version}/workspaces/${workspace.id}`,
            "DELETE",
            {})
    }

    /**
     * updates Workspace name and public flag
     * @param workspace
     * @param propertiesList
     * @returns {Promise<T>}
     */
    async apiUpdateWorkspace( workspace, propertiesList = null ){
        let data = {
            id: workspace.id,
        }

        // default properies to update
        if (!propertiesList) {
            data.name = workspace.name
            data.public = workspace.public
            data.publicDownload = workspace.publicDownload
            data.description = workspace.description
        } else {
            for (let p of propertiesList) {
                let v = workspace[p]
                if (v instanceof L.LatLngBounds) {
                    v = {
                        min: v.getNorthWest(),
                        max: v.getSouthEast()
                    }
                }
                data[p] = v
            }
        }

        return this._apiRequest(`/${this.version}/workspaces/${workspace.id}`, "PUT", data)
    }

    /**
     * return array of tokens which is used to build sharing weblink
     * @param workspaceId
     * @returns {Promise<T>}
     */
    async apiGetWorkspaceShareTokens( workspaceId ){
        return this._apiRequest(
            `/${this.version}/workspaces/${workspaceId}/shareTokens`,
            "GET",
            {}).then( apiData => {
                return apiData.data
        })
    }

    /**
     * sends notification about geometry operations
     * @param workspace
     * @param geometryType
     * @param operation
     * @param items
     * @returns {Promise<T>}
     */
    async apiNoticeGeometry( workspace, geometryType, operation, items ){
        return this._apiRequest(
            "/" + this.version + "/notification",
            "POST",
            {
                type: CN_TYPE_GEOMETRY_OP,
                payload: {
                    workspaceId: workspace.id,
                    operation: operation,
                    type: geometryType,
                    items: items,
                },
            })
    }

    /**
     * async
     * @param uri
     * @param method ("POST", "GET", etc)
     * @param data (object)
     * @returns {Promise<T>} returns data as object on success, or throw error with errorObject{code, detail, message, data} on errors
     * @private
     */
    _apiRequest(uri, method, data) {
        return new Promise(function(resolve, reject) {
            let hr = new XMLHttpRequest();
            if (!hr) {
                reject(new Error("Cannot create an XML HTTP instance"));
            }
            let dataToSend = (method === "GET") ? "" : JSON.stringify(data);
            if (method === "GET" && data) {
                let params = new URLSearchParams(data).toString()
                if (params !== "") {
                    uri += "?" + params;
                }
            }
            hr.open(method, uri, true);
            hr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
            hr.setRequestHeader("x-mapper-client-context-id", this.clientConxtextID);
            hr.setRequestHeader("x-mapper-request-id", this.requestNewContextID());

            let token = this._apiTokenFromCookie();
            if (token)
                hr.setRequestHeader("Authorization", "Bearer " + token);

            hr.onreadystatechange = function() {
                if (hr.readyState === XMLHttpRequest.DONE) {
                    let obj = null;
                    try {
                        obj = JSON.parse(hr.responseText);
                    } catch (e) {
                        let err = new Error("We got an incorrect response with status: " + hr.status)
                        err.code = hr.status
                        reject(err)
                        console.error(`Incorrect response at ${method} ${uri}: " + hr.responseText`)
                        return;
                    }
                    if (obj.code === 0 || obj.data) {
                        resolve(obj)
                    } else {
                        this.elog("ERROR", `Backend responses with error ${obj.code} at ${method} ${uri}: ${obj.message}`, obj)
                        reject(obj)
                    }
                }
            }.bind(this);
            hr.send(dataToSend);
        }.bind(this));
    }

    _apiTokenFromCookie() {
        return this.getCookie("apiKey")
    }

    getCookie(name) {
        let matches = document.cookie.match(new RegExp(
            "(?:^|; )" + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)"
        ));
        return matches ? decodeURIComponent(matches[1].replace( /\+/g, "%20")) : undefined;
    }

    getOneTimeCookie(name) {
        let val = getCookie(name);
        if (val != undefined)
            deleteCookie(name);
        return val;
    }

    deleteAllCookies() {
        var cookies = document.cookie.split(";");

        for (var i = 0; i < cookies.length; i++) {
            var cookie = cookies[i];
            var eqPos = cookie.indexOf("=");
            var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            this.deleteCookie(name);
            //document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }
    }

    deleteCookie(name) {
        document.cookie = `${name}=; Max-Age=0; Path=/${this.version}`;
    }

    //simple cookie setter
    //do not use special symbols,
    // need to refactor and add uri encoding
    setCookie(name, val, age, path) {
        let cookie = `${name}=${val}; Domain=${location.host}; Secure`;
        if (age != null)
            cookie += "; Max-Age="+age
        if (path != null)
            cookie += "; Path="+path;
        else
            cookie += "; Path=/";
        document.cookie = cookie;
    }

    clientNewContextID() {
        return this._uuidv4('axxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx')
    }

    requestNewContextID() {
        return this._uuidv4('bxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx')
    }

    //simple uid generator, it's enough to our purpose
    _uuidv4(format) {
        return format.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}
